import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Ticket } from 'lucide-react';
import type { CouponPublic, CouponClaimPublic } from '@evi/shared';
import { formatLosAngelesDate } from '@evi/shared';
import { StatusPill } from '@/components/primitives';
import { fetchCoupons, claimCoupon } from '@/lib/api';
import { couponClaimPill } from '@/lib/couponClaimPill';
import NamedIcon from '@/components/ui/NamedIcon';
import styles from './CouponsPage.module.css';

interface Props { onNavigate: (path: string) => void }

type Filter = 'all' | 'available' | 'claimed';

export function CouponsPage({ onNavigate }: Props) {
  const { t } = useTranslation('coupons');
  const qc = useQueryClient();
  const [filter, setFilter] = useState<Filter>('all');

  const { data, isLoading, error } = useQuery({
    queryKey: ['coupons', 'driver'],
    queryFn: fetchCoupons,
    staleTime: 30_000,
  });

  const claim = useMutation({
    mutationFn: (couponId: string) => claimCoupon({ couponId }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['coupons', 'driver'] }),
  });

  const available = data?.available ?? [];
  const claims = data?.claims ?? [];

  const visible = useMemo(() => {
    // Combined list sorted most-useful-first: available coupons (the
    // driver can act on these) then their existing claims. Each card
    // binds to either a CouponPublic (available) or a CouponClaim
    // (claimed) via the `claimRow` discriminator.
    type Row =
      | { kind: 'available'; coupon: CouponPublic }
      | { kind: 'claimed'; claim: CouponClaimPublic };

    const rows: Row[] = [];
    if (filter === 'all' || filter === 'available') {
      for (const c of available) rows.push({ kind: 'available', coupon: c });
    }
    if (filter === 'all' || filter === 'claimed') {
      for (const cl of claims) rows.push({ kind: 'claimed', claim: cl });
    }
    return rows;
  }, [available, claims, filter]);

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <button
          type="button"
          className={styles.backFab}
          aria-label={t('header.back')}
          onClick={() => onNavigate('/map')}
        >
          <ArrowLeft size={16} />
        </button>
        <div className={styles.titleBlock}>
          <h1 className={styles.title}>{t('header.title')}</h1>
          <p className={styles.subtitle}>
            {t('header.subtitle', { available: available.length, claimed: claims.length })}
          </p>
        </div>
      </header>

      <div className={styles.filters} role="tablist">
        <FilterPill active={filter === 'all'} onClick={() => setFilter('all')}>{t('filter.all')}</FilterPill>
        <FilterPill active={filter === 'available'} onClick={() => setFilter('available')}>{t('filter.available')}</FilterPill>
        <FilterPill active={filter === 'claimed'} onClick={() => setFilter('claimed')}>{t('filter.claimed')}</FilterPill>
      </div>

      {isLoading && <CouponSkeleton />}

      {!isLoading && error && (
        <div className={styles.errorBox}>
          {error instanceof Error ? error.message : t('error.load')}
        </div>
      )}

      {!isLoading && !error && visible.length === 0 && (
        <EmptyCoupons filter={filter} onExplore={() => onNavigate('/map')} />
      )}

      {!isLoading && !error && visible.length > 0 && (
        <div className={styles.list}>
          {visible.map((row) =>
            row.kind === 'available' ? (
              <CouponCard
                key={`coupon-${row.coupon.id}`}
                coupon={row.coupon}
                claim={null}
                onClaim={() => claim.mutate(row.coupon.id)}
                pending={claim.isPending && claim.variables === row.coupon.id}
              />
            ) : (
              <CouponCard
                key={`claim-${row.claim.id}`}
                coupon={row.claim.coupon}
                claim={row.claim}
                onClaim={() => {}}
                pending={false}
              />
            ),
          )}
        </div>
      )}
    </div>
  );
}

// ── Card ──────────────────────────────────────────────────────────────

function CouponCard({
  coupon, claim, onClaim, pending,
}: {
  coupon: CouponPublic;
  claim: CouponClaimPublic | null;
  onClaim: () => void;
  pending: boolean;
}) {
  const { t } = useTranslation('coupons');
  const claimed = claim !== null || coupon.alreadyClaimed;
  // Applied, Expired (unused and past its last day) or Claimed.
  const pill = couponClaimPill(claim?.status);
  const scopeLabel =
    coupon.appliesTo === 'session' ? t('scope.charging')
    : coupon.appliesTo === 'wallet_topup' ? t('scope.topup')
    : t('scope.both');

  return (
    <article className={styles.card}>
      <div className={styles.cardTop}>
        <div className={styles.cardInfo}>
          <p className={styles.cardScope}>{scopeLabel}</p>
          <p className={styles.cardName}>{coupon.name}</p>
          {coupon.description && (
            <p className={styles.cardDesc}>{coupon.description}</p>
          )}
        </div>
        <span className={styles.cardDiscount}>{formatDriverDiscount(coupon, t)}</span>
      </div>

      <div className={styles.cardFoot}>
        <code className={styles.cardCode}>{coupon.code}</code>
        {coupon.validUntil && (
          <span className={styles.cardExpiry}>
            {t('card.until', { date: formatDate(coupon.validUntil) })}
          </span>
        )}
        {claimed ? (
          <StatusPill tone={pill.tone} variant="soft" size="sm">
            {t(pill.labelKey)}
          </StatusPill>
        ) : (
          <button
            type="button"
            className={styles.claimBtn}
            onClick={onClaim}
            disabled={pending}
            aria-label={t('card.claimAria', { code: coupon.code })}
          >
            {pending ? t('card.claiming') : t('card.claim')}
          </button>
        )}
      </div>
    </article>
  );
}

// ── Filter pill ───────────────────────────────────────────────────────

function FilterPill({
  active, onClick, children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      className={`${styles.filterPill} ${active ? styles.filterPillActive : ''}`}
      onClick={onClick}
    >
      {children}
    </button>
  );
}

// ── States ────────────────────────────────────────────────────────────

function CouponSkeleton() {
  return (
    <div className={styles.list} aria-busy aria-live="polite">
      {Array.from({ length: 3 }).map((_, i) => (
        <div key={i} className={styles.cardSkeleton} />
      ))}
    </div>
  );
}

function EmptyCoupons({
  filter, onExplore,
}: { filter: Filter; onExplore: () => void }) {
  const { t } = useTranslation('coupons');
  const msg =
    filter === 'claimed' ? t('empty.claimed')
    : filter === 'available' ? t('empty.available')
    : t('empty.all');
  return (
    <div className={styles.empty}>
      <div className={styles.emptyIcon} aria-hidden>
        {/* size 80 vs the prior 40 so the ticket Lottie dominates
            its slot. The .emptyIcon wrapper drops its circle bg +
            box-shadow on this page (see CouponsPage.module.css)
            so the icon reads alone rather than competing with a
            visible backing circle. Other PWA empty states keep
            their tinted circles untouched. */}
        <NamedIcon name="ticket" fallback={<Ticket size={80} />} size={80} />
      </div>
      <h2 className={styles.emptyTitle}>{msg}</h2>
      <p className={styles.emptyDesc}>
        {t('empty.desc')}
      </p>
      {filter === 'available' && (
        <button type="button" className={styles.emptyCta} onClick={onExplore}>
          {t('empty.cta')}
        </button>
      )}
    </div>
  );
}

// ── Formatters ────────────────────────────────────────────────────────

function formatDriverDiscount(
  c: CouponPublic,
  t: ReturnType<typeof useTranslation>['t'],
): string {
  if (c.discountType === 'percentage') return t('discount.percent', { value: c.discountValue });
  if (c.discountType === 'fixed_amount') return t('discount.amount', { value: c.discountValue.toFixed(0) });
  return t('discount.points', { value: c.discountValue });
}

/** The coupon's last day: the Los Angeles day it ends on (23:59:59 there),
 *  the same day for a driver in any zone. */
function formatDate(iso: string): string {
  try { return formatLosAngelesDate(iso, undefined, { month: 'short', day: 'numeric' }); }
  catch { return iso; }
}
